/**
 * ApiError class to handle the errors from the api.
 * With the rewriting of the iOT services this will be
 * the place where the errors will be caught and handle
 * @extends Error
 */
export class ApiError extends Error {
	status: number;
	response: any;

	constructor(message: string, status: number, response?: any) {
		super(message);
		this.status = status;
		this.response = response;
	}

	/**
	 * Copies fetch Response headers into a plain object (for error payloads).
	 */
	private static responseHeadersToObject(response: Response): Record<string, string> {
		const headers: Record<string, string> = {};
		response.headers.forEach((value, key) => {
			headers[key] = value;
		});
		return headers;
	}

	/**
	 * Handles API response and throws an ApiError if necessary.
	 * @param response - Response from fetch API
	 * @returns Parsed JSON data
	 */
	static async handleResponse(response: Response) {
		let data: object;

		try {
			// when download invoice response type is blob instead of json
			const contentType = response.headers.get('content-type');

			if (contentType === 'application/pdf') {
				data = await response.blob();
			} else {
				data = await response.json();
			}
		} catch (error) {
			if (response.status >= 200 && response.status < 300) {
				return null;
			}

			const headers = ApiError.responseHeadersToObject(response);
			throw new ApiError('Request error occurred.', response.status, {
				data: null,
				headers,
			});
		}

		if (response.status >= 400 || !response.ok) {
			const headers = ApiError.responseHeadersToObject(response);
			const message = (data as { message?: string })?.message || 'Request error occurred.';
			throw new ApiError(message, response.status, {
				data,
				headers,
			});
		}

		return data;
	}

	/**
	 * Handles fetch errors (network issues, failed requests).
	 * @param error - Error thrown by fetch
	 * @param status - HTTP status (if available)
	 * @returns Formatted error object
	 */
	static handleFetchError(error: any, status?: number, inPromise = false) {
		const errorObject = {
			isok: false,
			response: error?.response,
			status: error?.status || status,
			error: `Error: Request failed with code ${error?.status || status}.`,
		};

		if (inPromise) return errorObject;

		return Promise.reject(errorObject);
	}
}
